// @ts-nocheck
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { CheckCircle2, Loader2, X } from 'lucide-react';
import type {
  ExecutionLogEntry,
  ExecutionProgress,
  ExecutionSummary,
} from '../../../hooks/useDeploymentExecution';

interface DeploymentExecutionDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: string | null;
  progress: ExecutionProgress;
  logs: ExecutionLogEntry[];
  completed: boolean;
  summary: ExecutionSummary | null;
  socketConnected: boolean;
}

export const DeploymentExecutionDialog = ({
  open,
  onClose,
  jobId,
  progress,
  logs,
  completed,
  summary,
  socketConnected,
}: DeploymentExecutionDialogProps) => {
  const percent =
    progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {completed ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            )}
            {completed ? 'Exécution terminée' : 'Création des tickets en cours'}
          </DialogTitle>
          <DialogDescription>
            {jobId ? `Job ${jobId}` : ''}
            {!completed && (
              <span className="ml-2 text-xs text-gray-400">
                {socketConnected ? '· temps réel' : '· synchronisation…'}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>
                {progress.current} / {progress.total}
              </span>
              <span>{percent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  completed
                    ? summary && summary.failed > 0
                      ? 'bg-orange-500'
                      : 'bg-green-500'
                    : 'bg-primary'
                }`}
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>

          {completed && summary && (
            <div className="bg-gray-50 border rounded p-3 text-sm">
              <p className="font-medium">Résumé</p>
              <p className="text-gray-600">
                {summary.created} créés · {summary.failed} échecs · {summary.skipped} ignorés · {summary.total} total
              </p>
            </div>
          )}

          {logs.length > 0 && (
            <div className="max-h-60 overflow-y-auto border rounded bg-gray-50 p-2">
              <ul className="space-y-1 text-xs font-mono">
                {logs.slice(-100).map((log, idx) => {
                  const color =
                    log.type === 'created'
                      ? 'text-green-700'
                      : log.type === 'skipped'
                      ? 'text-yellow-700'
                      : 'text-red-700';
                  return (
                    <li key={idx} className={color}>
                      {log.type === 'created'
                        ? `✓ Item ${log.itemId} → ${log.issueKey}`
                        : log.type === 'skipped'
                        ? `⊘ Item ${log.itemId} déjà couvert → ${log.issueKey}`
                        : `✗ Item ${log.itemId} : ${log.error}`}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              {completed ? 'Fermer' : 'Réduire'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
